// ==========================================================
// Tutorial / Guide Modal Controller (How to Play & Tool Guide)
// ==========================================================

import { i18n } from '../i18n/I18nService';
import { sounds } from '../audio/SoundEffects';

export type TutorialTab = 'objective' | 'tools' | 'masking' | 'controls';

export class TutorialModalController {
  private modalEl: HTMLElement;
  private currentTab: TutorialTab = 'objective';
  private tabs: TutorialTab[] = ['objective', 'tools', 'masking', 'controls'];

  // Elements
  private tabButtons: NodeListOf<HTMLButtonElement>;
  private tabPanels: NodeListOf<HTMLElement>;
  private btnClose: HTMLButtonElement;
  private btnFooterClose: HTMLButtonElement;
  private btnStart: HTMLButtonElement;
  private btnPrev: HTMLButtonElement;
  private btnNext: HTMLButtonElement;
  private modalTitleEl: HTMLElement;
  private modalSubEl: HTMLElement;

  private onStartCallback?: () => void;

  constructor() {
    this.modalEl = document.getElementById('modal-tutorial')!;
    this.tabButtons = document.querySelectorAll('.tutorial-tab-btn');
    this.tabPanels = document.querySelectorAll('.tutorial-tab-panel');
    this.btnClose = document.getElementById('btn-close-tutorial') as HTMLButtonElement;
    this.btnFooterClose = document.getElementById('btn-tutorial-footer-close') as HTMLButtonElement;
    this.btnStart = document.getElementById('btn-tutorial-start') as HTMLButtonElement;
    this.btnPrev = document.getElementById('btn-tutorial-prev') as HTMLButtonElement;
    this.btnNext = document.getElementById('btn-tutorial-next') as HTMLButtonElement;
    this.modalTitleEl = document.getElementById('tutorial-modal-title')!;
    this.modalSubEl = document.getElementById('tutorial-modal-sub')!;

    this.initEvents();
    this.renderTexts();

    // Subscribe to i18n changes
    i18n.subscribe(() => {
      this.renderTexts();
    });
  }

  private initEvents(): void {
    // 1. Tab buttons
    this.tabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        sounds.playClick();
        const tab = btn.getAttribute('data-tab') as TutorialTab;
        this.switchTab(tab);
      });
    });

    // 2. Navigation buttons
    this.btnPrev?.addEventListener('click', () => {
      sounds.playClick();
      const currentIndex = this.tabs.indexOf(this.currentTab);
      if (currentIndex > 0) {
        this.switchTab(this.tabs[currentIndex - 1]);
      }
    });

    this.btnNext?.addEventListener('click', () => {
      sounds.playClick();
      const currentIndex = this.tabs.indexOf(this.currentTab);
      if (currentIndex < this.tabs.length - 1) {
        this.switchTab(this.tabs[currentIndex + 1]);
      }
    });

    // 3. Close & Start
    const closeHandler = () => {
      sounds.playClick();
      this.close();
    };

    this.btnClose?.addEventListener('click', closeHandler);
    this.btnFooterClose?.addEventListener('click', closeHandler);

    this.btnStart?.addEventListener('click', () => {
      sounds.playClick();
      this.close();
      if (this.onStartCallback) this.onStartCallback();
    });
  }

  public setOnStartCallback(cb: () => void): void {
    this.onStartCallback = cb;
  }

  public open(defaultTab: TutorialTab = 'objective'): void {
    this.switchTab(defaultTab);
    this.modalEl.classList.remove('hidden');
    localStorage.setItem('paint_meister_tutorial_seen', 'true');
  }

  public close(): void {
    this.modalEl.classList.add('hidden');
  }

  public switchTab(tab: TutorialTab): void {
    this.currentTab = tab;
    this.tabButtons.forEach(btn => {
      btn.classList.toggle('active', btn.getAttribute('data-tab') === tab);
    });
    this.tabPanels.forEach(panel => {
      panel.classList.toggle('active', panel.getAttribute('data-panel') === tab);
    });

    // Update Next / Prev button states
    const idx = this.tabs.indexOf(tab);
    if (this.btnPrev) {
      this.btnPrev.disabled = idx === 0;
      this.btnPrev.style.opacity = idx === 0 ? '0.4' : '1';
    }
    if (this.btnNext) {
      this.btnNext.disabled = idx === this.tabs.length - 1;
      this.btnNext.style.opacity = idx === this.tabs.length - 1 ? '0.4' : '1';
    }
  }

  public renderTexts(): void {
    const t = i18n.dict.tutorial;
    if (this.modalTitleEl) this.modalTitleEl.textContent = t.modalTitle;
    if (this.modalSubEl) this.modalSubEl.textContent = t.modalSub;
    if (this.btnFooterClose) this.btnFooterClose.textContent = t.closeBtn;
    if (this.btnStart) this.btnStart.textContent = t.startBtn;

    // Tab buttons text
    this.tabButtons.forEach(btn => {
      const tab = btn.getAttribute('data-tab') as keyof typeof t.tabs;
      if (t.tabs[tab]) {
        btn.textContent = t.tabs[tab];
      }
    });

    // Render Tab 1: Objective
    const objSec = t.sections.objective;
    const objEl = document.getElementById('tutorial-panel-objective');
    if (objEl) {
      objEl.innerHTML = `
        <div class="tut-section-header">
          <span class="tut-badge">${objSec.badge}</span>
          <h3 class="tut-headline">${objSec.title}</h3>
          <p class="tut-desc">${objSec.desc}</p>
        </div>
        <div class="tut-steps-grid">
          ${objSec.steps.map((st, i) => `
            <div class="tut-step-card">
              <div class="tut-step-num-bubble">${i + 1}</div>
              <h4 class="tut-step-title">${st.title}</h4>
              <p class="tut-step-desc">${st.desc}</p>
            </div>
          `).join('')}
        </div>
        <div class="tut-score-showcase">
          <div class="tut-badge-item">
            <span class="tut-score-icon">🎯</span>
            <span class="tut-score-text">${i18n.dict.result.accuracyLabel}</span>
          </div>
          <div class="tut-badge-item">
            <span class="tut-score-icon">✨</span>
            <span class="tut-score-text">${i18n.dict.result.finishLabel}</span>
          </div>
          <div class="tut-badge-item">
            <span class="tut-score-icon">⏱️</span>
            <span class="tut-score-text">${i18n.dict.result.speedLabel}</span>
          </div>
          <div class="tut-badge-item">
            <span class="tut-score-icon">🏆</span>
            <span class="tut-score-text">${i18n.dict.result.stamps.meister} ★★★</span>
          </div>
        </div>
      `;
    }

    // Render Tab 2: Tools
    const toolsSec = t.sections.tools;
    const toolsEl = document.getElementById('tutorial-panel-tools');
    if (toolsEl) {
      toolsEl.innerHTML = `
        <div class="tut-section-header">
          <span class="tut-badge">${toolsSec.badge}</span>
          <h3 class="tut-headline">${toolsSec.title}</h3>
          <p class="tut-desc">${toolsSec.desc}</p>
        </div>
        <div class="tut-tools-grid">
          ${toolsSec.tools.map(tool => `
            <div class="tut-tool-card">
              <div class="tut-tool-icon">${tool.icon}</div>
              <div class="tut-tool-info">
                <div class="tut-tool-title-row">
                  <span class="tut-tool-name">${tool.name}</span>
                  <span class="tut-tool-role">${tool.role}</span>
                </div>
                <p class="tut-tool-desc">${tool.desc}</p>
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }

    // Render Tab 3: Masking
    const maskSec = t.sections.masking;
    const maskEl = document.getElementById('tutorial-panel-masking');
    if (maskEl) {
      maskEl.innerHTML = `
        <div class="tut-section-header">
          <span class="tut-badge">${maskSec.badge}</span>
          <h3 class="tut-headline">${maskSec.title}</h3>
          <p class="tut-desc">${maskSec.desc}</p>
        </div>
        <div class="tut-masking-flow">
          ${maskSec.steps.map(step => `
            <div class="tut-masking-card">
              <span class="tut-masking-step-badge">${step.num}</span>
              <h4 class="tut-masking-title">${step.title}</h4>
              <p class="tut-masking-desc">${step.desc}</p>
            </div>
          `).join('')}
        </div>
        <div class="tut-tip-banner">
          <span>💡 <strong>Pro Tip:</strong> ${i18n.getLanguage() === 'ja' ? '「全剥がし」ボタンで貼り付けたすべてのテープを一括で剥がせます！' : 'Tap "Peel All" to instantly strip all placed masking tapes at once!'}</span>
        </div>
      `;
    }

    // Render Tab 4: Controls & Lab
    const ctrlSec = t.sections.controls;
    const ctrlEl = document.getElementById('tutorial-panel-controls');
    if (ctrlEl) {
      ctrlEl.innerHTML = `
        <div class="tut-section-header">
          <span class="tut-badge">${ctrlSec.badge}</span>
          <h3 class="tut-headline">${ctrlSec.title}</h3>
          <p class="tut-desc">${ctrlSec.desc}</p>
        </div>
        <div class="tut-tips-list">
          ${ctrlSec.tips.map(tip => `
            <div class="tut-tip-card">
              <h4 class="tut-tip-title">${tip.title}</h4>
              <p class="tut-tip-desc">${tip.desc}</p>
            </div>
          `).join('')}
        </div>
      `;
    }
  }
}
